import axios from "../../../../api/axios";
import { toast } from 'react-toastify';

export const VIR_ENDPOINTS = {
  REPORTS_BASE: '/v2/vendor-inspection-reports/report',
  REPORT_BY_ID: '/v2/vendor-inspection-reports/report/:id',
  REPORT_SIGNED_URLS: '/v2/vendor-inspection-reports/report/signed-urls',
  REPORT_UPLOAD_REQUEST: '/v2/vendor-inspection-reports/report/:id/attachments/request-upload',
  REPORT_CONFIRM_UPLOAD: '/v2/vendor-inspection-reports/report/:id/attachments/confirm',
  REPORT_FORWARD: '/v2/vendor-inspection-reports/report/:id/forward',
  REPORT_STATUS_COUNTS: '/v2/vendor-inspection-reports/report/status-counts',
  CATEGORIES_BASE: '/v2/vendor-inspection-reports/category',
  CATEGORY_BY_ID: '/v2/vendor-inspection-reports/category/:id',
  VENDORS_BASE: '/v2/vendor-inspection-reports/vendor',
  VENDOR_BY_ID: '/v2/vendor-inspection-reports/vendor/:id',
  VENDORS_BY_CATEGORY: '/v2/vendor-inspection-reports/vendor/by-category/:categoryId',
};

const handleApiError = (error, defaultMessage) => {
  const message = error.response?.data?.message || error.message || defaultMessage || 'An unexpected error occurred';
  console.error(defaultMessage, error);
  toast.error(message);
  throw new Error(message);
};

export const getVIRReports = async (params = {}) => {
  try {
    const response = await axios.get(VIR_ENDPOINTS.REPORTS_BASE, { params });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch vendor inspection reports');
  }
};

export const getVIRReportById = async (id) => {
  try {
    const url = VIR_ENDPOINTS.REPORT_BY_ID.replace(':id', id);
    const response = await axios.get(url);
    return response.data.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch vendor inspection report');
  }
};


export const getVIRStatusCounts = async (params = {}) => {
  try {
    const response = await axios.get(VIR_ENDPOINTS.REPORT_STATUS_COUNTS, { params });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch VIR status counts');
  }
};

export const createVIRReport = async (payload) => {
  try {
    const response = await axios.post(VIR_ENDPOINTS.REPORTS_BASE, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to create vendor inspection report');
  }
};

export const updateVIRReport = async (id, payload) => {
  try {
    const url = VIR_ENDPOINTS.REPORT_BY_ID.replace(':id', id);
    const response = await axios.patch(url, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to update vendor inspection report');
  }
};

export const deleteVIRReport = async (id) => {
  try {
    const url = VIR_ENDPOINTS.REPORT_BY_ID.replace(':id', id);
    const response = await axios.delete(url);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to delete vendor inspection report');
  }
};

export const forwardVIRReport = async (id, payload) => {
  try {
    const url = VIR_ENDPOINTS.REPORT_FORWARD.replace(':id', id);
    const response = await axios.post(url, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to forward vendor inspection report');
  }
};

export const requestVIRUploadUrl = async (reportId, fileName, contentType, uploadType = 'attachment') => {
  try {
    const url = VIR_ENDPOINTS.REPORT_UPLOAD_REQUEST.replace(':id', reportId);
    const response = await axios.post(url, { fileName, contentType, uploadType });
    return response.data.data;
  } catch (error) {
    handleApiError(error, 'Failed to obtain upload URL');
  }
};

export const confirmVIRUpload = async (reportId, key, uploadType = 'attachment') => {
  try {
    const url = VIR_ENDPOINTS.REPORT_CONFIRM_UPLOAD.replace(':id', reportId);
    const response = await axios.post(url, { key, uploadType });
    return response.data.data;
  } catch (error) {
    handleApiError(error, 'Failed to confirm upload');
  }
};

export const getVIRSignedUrls = async (keys) => {
  if (!Array.isArray(keys) || keys.length === 0) return {};
  try {
    const response = await axios.post(VIR_ENDPOINTS.REPORT_SIGNED_URLS, { keys });
    return response.data.data;
  } catch (error) {
    handleApiError(error, 'Failed to generate signed URLs');
    return {};
  }
};

export const getVIRCategories = async (params = {}) => {
  try {
    const response = await axios.get(VIR_ENDPOINTS.CATEGORIES_BASE, { params });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch categories');
  }
};

export const createVIRCategory = async (payload) => {
  try {
    const response = await axios.post(VIR_ENDPOINTS.CATEGORIES_BASE, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to create category');
  }
};

export const updateVIRCategory = async (id, payload) => {
  try {
    const url = VIR_ENDPOINTS.CATEGORY_BY_ID.replace(':id', id);
    const response = await axios.patch(url, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to update category');
  }
};

export const deleteVIRCategory = async (id) => {
  try {
    const url = VIR_ENDPOINTS.CATEGORY_BY_ID.replace(':id', id);
    const response = await axios.delete(url);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to delete category');
  }
};

export const getVIRVendors = async (params = {}) => {
  try {
    const response = await axios.get(VIR_ENDPOINTS.VENDORS_BASE, { params });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch vendors');
  }
};

export const getVIRVendorsByCategory = async (categoryId) => {
  try {
    const url = VIR_ENDPOINTS.VENDORS_BY_CATEGORY.replace(':categoryId', categoryId);
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch vendors by category');
  }
};

export const getVIRVendorById = async (id) => {
  try {
    const url = VIR_ENDPOINTS.VENDOR_BY_ID.replace(':id', id);
    const response = await axios.get(url);
    return response.data.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch vendor');
  }
};

export const createVIRVendor = async (payload) => {
  try {
    const response = await axios.post(VIR_ENDPOINTS.VENDORS_BASE, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to create vendor');
  }
};

export const updateVIRVendor = async (id, payload) => {
  try {
    const url = VIR_ENDPOINTS.VENDOR_BY_ID.replace(':id', id);
    const response = await axios.patch(url, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to update vendor');
  }
};

export const deleteVIRVendor = async (id) => {
  try {
    const url = VIR_ENDPOINTS.VENDOR_BY_ID.replace(':id', id);
    const response = await axios.delete(url);
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to delete vendor');
  }
};
